import React from 'react';
import {IPN_ACTION, type IpnAction} from '@packages/systems/telemetry';
import {
  type TextColor,
  type IconComponent,
  type LinkTargets,
} from '@packages/systems/spring';
import {BrandedButton} from '@packages/systems/spring-branded';
import {emotionStyled} from '@packages/utilities/styled';
import {Button} from '@packages/systems/spring';
import {type GuideButton} from './knockGuides/guideMessageTypeSchemas/guideButton';
import {knockIconMap} from './knockIconMap';
import {KnockButtonAction} from './KnockButtonAction';

type GuideButtonProps = {
  button: GuideButton;
  buttonVariant?: 'spring' | 'branded-font' | 'branded-full';
  onClick: (action: IpnAction) => void;
  iconColorVariant?: TextColor;
  linkIcon?: IconComponent;
} & Omit<React.ComponentProps<typeof Button>, 'onClick'>;

const BrandedSpringButton = emotionStyled(Button)({
  fontFamily: '$brandedBody',
});

const buttonVariants = {
  spring: Button,
  'branded-font': BrandedSpringButton as typeof Button,
  'branded-full': BrandedButton,
} as const;

export function GuideButton({
  button,
  buttonVariant = 'spring',
  onClick,
  iconColorVariant = 'inverse',
  linkIcon: LinkIcon,
  ...props
}: GuideButtonProps) {
  const {action, text} = button;
  const Icon = knockIconMap[action];
  const ButtonComponent = buttonVariants[buttonVariant];
  const isLink = action.startsWith('http');
  const isDismiss = action === KnockButtonAction.Dismiss;
  const suffix = isLink
    ? LinkIcon && <LinkIcon colorVariant={iconColorVariant} />
    : Icon && <Icon colorVariant={iconColorVariant} />;

  // Only set href and target for actual URLs, not for special actions like "dismiss"
  const linkProps: {href?: string; target?: LinkTargets} =
    isLink && !isDismiss ? {href: action, target: '_blank' as LinkTargets} : {};

  return (
    <ButtonComponent
      {...linkProps}
      onClick={() => onClick(IPN_ACTION.CTA_BUTTON_CLICKED)}
      suffix={suffix}
      {...props}
    >
      {text}
    </ButtonComponent>
  );
}
